import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import he from 'he';
import Countdown from '../Countdown';
import Chatbot from '../Chatbot';
import { getLetter } from '../../utils';
import { FaChevronRight, FaCheck, FaTimes, FaPause, FaPlay, FaStop } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';

const Quiz = ({ data, countdownTime, endQuiz }) => {
  const { isDark } = useTheme();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [userSlectedAns, setUserSlectedAns] = useState(null);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState([]);
  const [timeTaken, setTimeTaken] = useState(0);
  const [showSolutions, setShowSolutions] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (questionIndex > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [questionIndex]);

  const handleItemClick = (answer) => {
    if (isPaused) return;
    setUserSlectedAns(answer);
  };

  const handleNext = () => {
    if (isPaused) return;
    const isCorrect = userSlectedAns === he.decode(data[questionIndex].correct_answer);
    const point = isCorrect ? 1 : 0;

    const qna = [...questionsAndAnswers, {
      id: data[questionIndex].id,
      question: he.decode(data[questionIndex].question),
      user_answer: userSlectedAns,
      correct_answer: he.decode(data[questionIndex].correct_answer),
      solution: data[questionIndex].solution,
      point,
    }];

    if (questionIndex === data.length - 1) {
      setShowSolutions(true);
      endQuiz({
        totalQuestions: data.length,
        correctAnswers: correctAnswers + point,
        timeTaken,
        questionsAndAnswers: qna,
      });
    } else {
      setCorrectAnswers(correctAnswers + point);
      setQuestionIndex(questionIndex + 1);
      setUserSlectedAns(null);
      setQuestionsAndAnswers(qna);
    }
  };

  const handleEndTest = () => {
    // Calculate score for current question if answered
    let finalScore = correctAnswers;
    let finalQna = [...questionsAndAnswers];

    if (userSlectedAns !== null) {
      const isCorrect = userSlectedAns === he.decode(data[questionIndex].correct_answer);
      const point = isCorrect ? 1 : 0;
      finalScore += point;

      finalQna = [...questionsAndAnswers, {
        id: data[questionIndex].id,
        question: he.decode(data[questionIndex].question),
        user_answer: userSlectedAns,
        correct_answer: he.decode(data[questionIndex].correct_answer),
        solution: data[questionIndex].solution,
        point,
      }];
    }

    // Mark remaining questions as unanswered
    for (let i = questionIndex + (userSlectedAns !== null ? 1 : 0); i < data.length; i++) {
      finalQna.push({
        id: data[i].id,
        question: he.decode(data[i].question),
        user_answer: null,
        correct_answer: he.decode(data[i].correct_answer),
        solution: data[i].solution,
        point: 0,
      });
    }

    setShowSolutions(true);
    endQuiz({
      totalQuestions: data.length,
      correctAnswers: finalScore,
      timeTaken,
      questionsAndAnswers: finalQna,
    });
  };

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  const timeOver = (time) => {
    endQuiz({
      totalQuestions: data.length,
      correctAnswers,
      timeTaken: time,
      questionsAndAnswers,
    });
  };

  const progressPercentage = ((questionIndex) / data.length) * 100;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark
        ? 'bg-dark-primary'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
      }`}>
      {/* Header Bar */}
      <div className={`sticky top-0 z-20 transition-colors duration-300 ${isDark
          ? 'bg-dark-secondary/90 backdrop-blur-xl border-b border-white/5'
          : 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-2 sm:gap-3 w-full justify-between">
              <h1 className="text-white text-lg sm:text-xl md:text-2xl font-bold">
                Question {questionIndex + 1} of {data.length}
              </h1>
              <div className="flex items-center gap-2">
                <motion.button
                  onClick={togglePause}
                  className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${isPaused
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md'
                      : 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-md'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isPaused ? <FaPlay className="text-xs" /> : <FaPause className="text-xs" />}
                  <span className="hidden sm:inline">{isPaused ? 'Resume' : 'Pause'}</span>
                </motion.button>
                {!showSolutions && (
                  <motion.button
                    onClick={handleEndTest}
                    className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all bg-red-500 text-white hover:bg-red-600 shadow-md"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaStop className="text-xs" />
                    <span className="hidden sm:inline">End Test</span>
                  </motion.button>
                )}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className={`mt-3 sm:mt-4 w-full h-2 sm:h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-white/20'
            }`}>
            <motion.div
              className="h-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 shadow-lg"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Pause Notice */}
        {isPaused && (
          <motion.div
            className={`mb-6 p-4 rounded-xl text-center shadow-md ${isDark
                ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                : 'bg-yellow-50 border-2 border-yellow-400 text-yellow-900'
              }`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-center gap-2">
              <FaPause className="text-lg" />
              <p className="font-semibold">Test paused. Resume to continue answering questions and the timer.</p>
            </div>
          </motion.div>
        )}

        {!showSolutions ? (
          <motion.div
            className={`rounded-2xl overflow-hidden ${isDark
                ? 'bg-dark-secondary/80 border border-white/5'
                : 'bg-white shadow-xl'
              }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            key={questionIndex}
          >
            {/* Question Card */}
            <div className="p-6 sm:p-8 md:p-10">
              <div className="mb-6 sm:mb-8">
                <div className="inline-block px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm font-semibold rounded-full mb-4">
                  Question {questionIndex + 1}
                </div>
                <h2 className={`text-lg sm:text-xl md:text-2xl font-semibold leading-relaxed ${isDark ? 'text-white' : 'text-gray-800'
                  }`}>
                  {he.decode(data[questionIndex].question)}
                </h2>
              </div>

              {/* Options Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {data[questionIndex].options.map((option, i) => {
                  const letter = getLetter(i);
                  const decodedOption = he.decode(option);
                  const isSelected = userSlectedAns === decodedOption;

                  return (
                    <motion.button
                      key={decodedOption}
                      onClick={() => handleItemClick(decodedOption)}
                      disabled={isPaused}
                      className={`group relative rounded-xl p-4 sm:p-5 text-base sm:text-lg transition-all duration-300 border-2 flex items-start gap-3 sm:gap-4 min-h-[60px] sm:min-h-[70px] ${isSelected
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 border-purple-600 text-white font-semibold shadow-lg'
                          : isDark
                            ? 'bg-dark-tertiary/50 border-white/10 text-gray-200 hover:border-purple-500/50 hover:bg-dark-tertiary'
                            : 'bg-gray-50 border-gray-200 text-gray-800 hover:border-purple-400 hover:bg-purple-50 hover:shadow-md'
                        } ${isPaused ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      whileHover={!isPaused ? { scale: 1.02 } : {}}
                      whileTap={!isPaused ? { scale: 0.98 } : {}}
                    >
                      <span className={`flex-shrink-0 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold text-sm sm:text-base transition-all ${isSelected
                          ? 'bg-white/30 text-white'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        }`}>
                        {letter}
                      </span>
                      <span className="text-left flex-1 leading-relaxed">{decodedOption}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Next Button */}
            <div className="px-6 sm:px-8 md:px-10 pb-6 sm:pb-8">
              <motion.button
                className={`w-full sm:w-auto sm:min-w-[200px] py-3 sm:py-3.5 px-6 sm:px-8 text-base sm:text-lg rounded-xl shadow-lg flex items-center justify-center gap-2 mx-auto font-semibold ${userSlectedAns
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-purple-500/25'
                    : isDark
                      ? 'bg-gray-700 cursor-not-allowed text-gray-500'
                      : 'bg-gray-300 cursor-not-allowed text-gray-500'
                  } ${isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleNext}
                disabled={!userSlectedAns || isPaused}
                whileHover={userSlectedAns && !isPaused ? { scale: 1.05 } : {}}
                whileTap={userSlectedAns && !isPaused ? { scale: 0.95 } : {}}
              >
                <span>{questionIndex === data.length - 1 ? 'Finish Quiz' : 'Next Question'}</span>
                <FaChevronRight className="text-sm" />
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            className={`rounded-2xl p-6 sm:p-8 md:p-10 ${isDark
                ? 'bg-dark-secondary/80 border border-white/5'
                : 'bg-white shadow-xl'
              }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                Quiz Results
              </h2>
              <div className={`inline-block p-6 sm:p-8 rounded-2xl ${isDark
                  ? 'bg-dark-tertiary/50 border border-white/10'
                  : 'bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200'
                }`}>
                {(() => {
                  const finalScore = questionsAndAnswers.reduce((sum, q) => sum + q.point, 0);
                  return (
                    <>
                      <div className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'
                        }`}>
                        {finalScore}/{data.length}
                      </div>
                      <div className={`text-lg sm:text-xl mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                        {((finalScore / data.length) * 100).toFixed(0)}% Score
                      </div>
                      <div className={`text-sm sm:text-base ${isDark ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                        Time: {Math.floor(timeTaken / 60)}:{(timeTaken % 60).toString().padStart(2, '0')}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="space-y-4 sm:space-y-5">
              {questionsAndAnswers.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={`p-4 sm:p-5 rounded-xl border-2 transition-all ${item.point
                      ? isDark
                        ? 'border-green-500/30 bg-green-500/5'
                        : 'border-green-500 bg-green-50'
                      : isDark
                        ? 'border-red-500/30 bg-red-500/5'
                        : 'border-red-500 bg-red-50'
                    }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start gap-3 sm:gap-4 mb-3">
                    <span className={`flex-shrink-0 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold text-sm sm:text-base text-white ${item.point ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                      {index + 1}
                    </span>
                    <h3 className={`font-semibold text-base sm:text-lg flex-1 ${isDark ? 'text-white' : 'text-gray-800'
                      }`}>{item.question}</h3>
                  </div>

                  <div className="ml-0 sm:ml-12 sm:ml-14 space-y-2">
                    {item.user_answer !== null ? (
                      <>
                        <div className="flex items-center gap-2 text-sm sm:text-base">
                          <span className={item.user_answer === item.correct_answer ? 'text-green-500' : 'text-red-500'}>
                            {item.user_answer === item.correct_answer ? <FaCheck /> : <FaTimes />}
                          </span>
                          <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-medium">Your answer:</span> {item.user_answer}
                          </span>
                        </div>

                        {item.user_answer !== item.correct_answer && (
                          <div className="flex items-center gap-2 text-sm sm:text-base text-green-500">
                            <FaCheck />
                            <span>
                              <span className="font-medium">Correct answer:</span> {item.correct_answer}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-sm sm:text-base text-orange-500">
                        <FaTimes />
                        <span className="font-medium">Not answered</span>
                      </div>
                    )}

                    {item.user_answer === null && (
                      <div className="flex items-center gap-2 text-sm sm:text-base text-green-500">
                        <FaCheck />
                        <span>
                          <span className="font-medium">Correct answer:</span> {item.correct_answer}
                        </span>
                      </div>
                    )}

                    {item.solution && (
                      <div className={`mt-3 p-3 sm:p-4 rounded-lg ${isDark
                          ? 'bg-dark-tertiary/50 border border-white/10'
                          : 'bg-white border border-gray-200'
                        }`}>
                        <p className={`text-xs sm:text-sm font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>Explanation:</p>
                        <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>{item.solution}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Countdown timer - Mobile optimized */}
      <div className="fixed right-3 sm:right-6 bottom-3 sm:bottom-6 z-10">
        <Countdown
          countdownTime={countdownTime}
          timeOver={timeOver}
          setTimeTaken={setTimeTaken}
          isPaused={isPaused}
        />
      </div>

      {/* AI Chatbot - Only show during active quiz */}
      {!showSolutions && (
        <Chatbot
          currentQuestion={he.decode(data[questionIndex].question)}
          currentOptions={data[questionIndex].options.map(opt => he.decode(opt))}
          questionNumber={questionIndex + 1}
          totalQuestions={data.length}
        />
      )}
    </div>
  );
};

Quiz.propTypes = {
  data: PropTypes.array.isRequired,
  countdownTime: PropTypes.number.isRequired,
  endQuiz: PropTypes.func.isRequired,
};

export default Quiz;